import React, { useEffect, useRef, useState, useCallback, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ChatInterface,
  useTheme,
  useChat,
  useAppDispatch,
  type Message,
  addMessage,
  usePooling,
  type MessageOption,
  setChatEnded as setChatEndedAction,
  useNotifications,
  removeNotification,
  markAllNotificationsAsRead,
  NotificationBadge,
  type FileUploadConfig,
  setLoading,
  abortRunningMessages,
} from 'chat-interface-lib';
import { useAuth } from '../contexts/AuthProvider';
import { useI18n } from '../contexts/I18nProvider';
import { useRefIntent } from 'chat-interface-lib';
import { useTenantConfig } from '../hooks/useTenantConfig';
import { useTenantIcons } from '../hooks/useTenantIcons';
import { ArrowLeft, Loader2, Maximize2, MessageCircle, Minimize2, RefreshCw, PhoneOff, Sun } from 'lucide-react';
import * as LucideIcons from 'lucide-react';
import { getAgentTranslation } from '../utils/agentTranslation';
import { apiService } from '../services/api';
import type { Agent as AgentType } from '../types';
import { getOrCreateAnonymousUserId } from '../utils/anonymousUserId';

interface AgentChatProps {
  isPublicMode?: boolean;
  publicAgent?: AgentType | null;
  publicAgentSid?: string;
}

const parseAllowedFileTypes = (value?: string): string[] => {
  if (!value) return [];
  return value
    .split(',')
    .map((t) => t.trim())
    .filter(Boolean)
    .map((t) => t.replace(/^\./, ''));
};

export const AgentChat: React.FC<AgentChatProps> = ({ isPublicMode = false, publicAgent = null, publicAgentSid }) => {
  const { agentId } = useParams<{ agentId: string }>();
  const navigate = useNavigate();
  const { theme, toggleTheme } = useTheme();
  const { userOid, user } = useAuth();
  const { actualLanguage } = useI18n();

  // Load tenant configuration
  const tenantSlug = import.meta.env.VITE_TENANT_SLUG || 'default';
  const { tenantData } = useTenantConfig(tenantSlug);
  const { getIcon } = useTenantIcons(tenantSlug);

  // Get icon components
  const ArrowLeftIcon = getIcon('arrowLeft', 'ArrowLeft') || ArrowLeft;
  const Loader2Icon = getIcon('loading', 'Loader2') || Loader2;
  const Maximize2Icon = getIcon('maximize', 'Maximize2') || Maximize2;
  const Minimize2Icon = getIcon('minimize', 'Minimize2') || Minimize2;
  const RefreshCwIcon = getIcon('refresh', 'RefreshCw') || RefreshCw;
  const PhoneOffIcon = getIcon('phoneOff', 'PhoneOff') || PhoneOff;
  const MessageCircleIcon = getIcon('messageCircle', 'MessageCircle') || MessageCircle;
  const MoonIcon = getIcon('themeToggle', 'Moon') || LucideIcons.Moon;
  const SunIcon = Sun; // Always use Sun for light mode

  // Local agent state (fetched directly from API)
  const [currentAgent, setCurrentAgent] = useState<AgentType | null>(isPublicMode ? publicAgent : null);
  const [agentLoading, setAgentLoading] = useState<boolean>(!isPublicMode);
  const [anonymousAgentSid, setAnonymousAgentSid] = useState<string | undefined>(publicAgentSid);
  const effectiveAgentId = useMemo(
    () => (isPublicMode ? (currentAgent?.id || 'default') : (agentId || 'default')),
    [isPublicMode, agentId, currentAgent?.id]
  );

  // Initialize anonymous agent_sid for public mode
  useEffect(() => {
    if (isPublicMode && currentAgent && !anonymousAgentSid) {
      const initializeAnonymousSid = async () => {
        const enablePersistence = currentAgent.config.VITE_ENABLE_PERSISTENCE ?? false;
        const sid = await getOrCreateAnonymousUserId(currentAgent.id, enablePersistence);
        setAnonymousAgentSid(sid);
      };
      initializeAnonymousSid();
    }
  }, [isPublicMode, currentAgent, anonymousAgentSid]);

  // Fetch agent by ID (only for authenticated mode)
  useEffect(() => {
    if (isPublicMode) {
      setAgentLoading(false);
      return;
    }
    
    const loadAgent = async () => {
      if (!agentId) return;
      const user = apiService.getStoredUser();
      if (!user?.tenantId) {
        setAgentLoading(false);
        return;
      }
      try {
        setAgentLoading(true);
        const dbAgents = await apiService.getTenantAgents(user.tenantId);
        const dbAgent = dbAgents.find((a: any) => a.id === agentId);
        if (!dbAgent) {
          setCurrentAgent(null);
          return;
        }

        const languages = await apiService.getTenantLanguages(user.tenantId);

        // Set agent directly from database
        setCurrentAgent({
          id: dbAgent.id,
          name: dbAgent.name,
          icon: dbAgent.icon,
          color: dbAgent.color,
          translations: dbAgent.translations,
          languages: languages,
          config: {
            VITE_CHAT_API_URL: dbAgent.chatApiUrl || '',
            VITE_CHAT_TITLE: dbAgent.name,
            VITE_AGENT_SID: dbAgent.agentSid || '',
            VITE_AGENT_SID_SOURCE: (dbAgent.agentSidSource || 'user_email') as 'oidc_user_id' | 'user_id' | 'user_email' | 'custom',
            VITE_CHAT_USER_ID: dbAgent.chatUserId || undefined,
            VITE_CUSTOM_FAVICON: dbAgent.customFavicon || undefined,
            VITE_STREAMING_ENABLED: dbAgent.streamingEnabled || undefined,
            VITE_ENABLE_PERSISTENCE: dbAgent.enablePersistence ?? undefined,
            VITE_ENABLE_PREEMPTIVE_END_CHAT_BUTTON: dbAgent.enablePreemptiveEndChatButton ?? undefined,
            VITE_ENABLE_CLEAR_CHAT_BUTTON: dbAgent.enableClearChatButton ?? undefined,
            VITE_SHOW_ROUTE_STACK: dbAgent.showRouteStack ?? undefined,
            VITE_FETCH_FOR_WELCOME_MESSAGE: dbAgent.fetchForWelcomeMessage ?? undefined,

            // Notification configuration
            VITE_ALLOW_NOTIFICATIONS: dbAgent.allowNotifications ?? undefined,
            VITE_FETCH_FOR_NOTIFICATION: dbAgent.fetchForNotification ?? undefined,
            VITE_NOTIFICATION_POOLING_FREQUENCY: dbAgent.notificationPoolingFrequency ?? undefined,

            // File upload configuration (chat input attachments)
            VITE_ALLOW_FILE_UPLOAD: dbAgent.allowFileUpload ?? undefined,
            VITE_ALLOW_IMAGE_PASTE: dbAgent.allowImagePaste ?? undefined,
            VITE_ALLOWED_FILE_TYPES: dbAgent.allowedFileTypes || undefined,
            VITE_MAX_FILE_SIZE: dbAgent.maxFileSize ?? undefined,
            VITE_MAX_FILES: dbAgent.maxFiles ?? undefined,

            // Theme configuration
            // Light theme
            VITE_THEME_PRIMARY: dbAgent.themePrimary || undefined,
            VITE_THEME_SECONDARY: dbAgent.themeSecondary || undefined,
            VITE_THEME_BACKGROUND: dbAgent.themeBackground || undefined,
            VITE_THEME_FOREGROUND: dbAgent.themeForeground || undefined,
            VITE_THEME_CARD: dbAgent.themeCard || undefined,
            VITE_THEME_CARD_FOREGROUND: dbAgent.themeCardForeground || undefined,
            VITE_THEME_PRIMARY_FOREGROUND: dbAgent.themePrimaryForeground || undefined,
            VITE_THEME_SECONDARY_FOREGROUND: dbAgent.themeSecondaryForeground || undefined,
            VITE_THEME_MUTED: dbAgent.themeMuted || undefined,
            VITE_THEME_MUTED_FOREGROUND: dbAgent.themeMutedForeground || undefined,
            VITE_THEME_BORDER: dbAgent.themeBorder || undefined,
            VITE_THEME_INPUT: dbAgent.themeInput || undefined,

            // Dark theme
            VITE_THEME_DARK_PRIMARY: dbAgent.themeDarkPrimary || undefined,
            VITE_THEME_DARK_SECONDARY: dbAgent.themeDarkSecondary || undefined,
            VITE_THEME_DARK_BACKGROUND: dbAgent.themeDarkBackground || undefined,
            VITE_THEME_DARK_FOREGROUND: dbAgent.themeDarkForeground || undefined,
            VITE_THEME_DARK_CARD: dbAgent.themeDarkCard || undefined,
            VITE_THEME_DARK_CARD_FOREGROUND: dbAgent.themeDarkCardForeground || undefined,
            VITE_THEME_DARK_PRIMARY_FOREGROUND: dbAgent.themeDarkPrimaryForeground || undefined,
            VITE_THEME_DARK_SECONDARY_FOREGROUND: dbAgent.themeDarkSecondaryForeground || undefined,
            VITE_THEME_DARK_MUTED: dbAgent.themeDarkMuted || undefined,
            VITE_THEME_DARK_MUTED_FOREGROUND: dbAgent.themeDarkMutedForeground || undefined,
            VITE_THEME_DARK_BORDER: dbAgent.themeDarkBorder || undefined,
            VITE_THEME_DARK_INPUT: dbAgent.themeDarkInput || undefined,

            // Feedback configuration
            VITE_FEEDBACK_ENABLED: dbAgent.feedbackEnabled || undefined,
            VITE_FEEDBACK_THUMBS_UP_URL: dbAgent.feedbackPositiveUrl || undefined,
            VITE_FEEDBACK_THUMBS_DOWN_URL: dbAgent.feedbackNegativeUrl || undefined,
            VITE_FEEDBACK_MIN_CHARS: dbAgent.feedbackMinChars ?? 20,
            VITE_RECAPTCHA_ENABLED: dbAgent.recaptchaEnabled ?? undefined,
            VITE_RECAPTCHA_SITE_KEY: dbAgent.recaptchaSiteKey || undefined,
            VITE_RECAPTCHA_VERSION: (dbAgent.recaptchaVersion as 'v2' | 'v3') || undefined,

            // Footnote configuration
            VITE_FOOTNOTE_ENABLED: dbAgent.footnoteEnabled || undefined,
            VITE_AGENT_MESSAGE_FOOTNOTE: dbAgent.agentMessageFootnote || undefined,
            VITE_FOOTNOTE_TITLE: dbAgent.footnoteTitle || undefined,
            VITE_FOOTNOTE_COLLAPSIBLE: dbAgent.footnoteCollapsible || undefined,
            VITE_FOOTNOTE_SHOW_TIMING: (dbAgent.footnoteShowTiming as 'immediately' | 'after_response' | 'let_agent_decide') || undefined,
          },
        });
      } catch (e) {
        // swallow - handled by not found state
      } finally {
        setAgentLoading(false);
      }
    };
    loadAgent();
  }, [agentId, actualLanguage]);

  // Get translated agent information (agent already comes with translations from backend)
  const agentTranslation = useMemo(() => currentAgent && (currentAgent?.languages?.length || 0) > 0
    ? getAgentTranslation(currentAgent, actualLanguage, currentAgent?.languages || [])
    : null, [currentAgent, actualLanguage]);

  // Calculate agentSid based on source
  const calculatedAgentSid = useMemo(() => {
    if (!currentAgent) return undefined;

    // In public mode, use anonymous agent_sid
    if (isPublicMode) {
      return anonymousAgentSid || undefined;
    }

    if (currentAgent.config.VITE_CHAT_USER_ID) {
      // Chat User ID takes precedence
      return currentAgent.config.VITE_CHAT_USER_ID;
    }

    const agentSidSource = (currentAgent.config as any).VITE_AGENT_SID_SOURCE || 'user_email';

    switch (agentSidSource) {
      case 'oidc_user_id':
        return userOid || undefined;
      case 'user_id':
        return user?.id || undefined;
      case 'user_email':
        return user?.email || undefined;
      case 'custom':
        return currentAgent.config.VITE_AGENT_SID || undefined;
      default:
        // Fallback to old logic for backward compatibility
        return userOid || currentAgent.config.VITE_AGENT_SID || undefined;
    }
  }, [currentAgent, userOid, user, isPublicMode, anonymousAgentSid]);

  // Initialize ref intent functionality only when user is logged in and agent is selected (skip in public mode)
  const { isLoading: refIntentLoading } = useRefIntent({
    agentId: effectiveAgentId,
    apiUrl: currentAgent?.config?.VITE_CHAT_API_URL,
    agentSid: userOid || undefined,
    enabled: !isPublicMode && !!userOid && !!currentAgent
  });

  // Memoize streamingConfig to prevent unnecessary re-renders
  const streamingConfig = useMemo(() => {
    return currentAgent?.config.VITE_STREAMING_ENABLED ? { enabled: true } : undefined;
  }, [currentAgent?.config.VITE_STREAMING_ENABLED]);

  // Memoize chat title and welcome message to prevent re-renders
  const chatTitle = useMemo(() => {
    return agentTranslation?.chatTitle || currentAgent?.config.VITE_CHAT_TITLE;
  }, [agentTranslation?.chatTitle, currentAgent?.config.VITE_CHAT_TITLE]);

  const welcomeMessage = useMemo(() => {
    return agentTranslation?.welcomeMessage || currentAgent?.config.VITE_WELCOME_MESSAGE;
  }, [agentTranslation?.welcomeMessage, currentAgent?.config.VITE_WELCOME_MESSAGE]);

  const fileUploadConfig: FileUploadConfig | undefined = useMemo(() => {
    const allowFileUpload = currentAgent?.config.VITE_ALLOW_FILE_UPLOAD ?? false;
    const allowImagePaste = currentAgent?.config.VITE_ALLOW_IMAGE_PASTE ?? false;
    const allowedFileTypes = parseAllowedFileTypes(currentAgent?.config.VITE_ALLOWED_FILE_TYPES);
    const maxFileSize = currentAgent?.config.VITE_MAX_FILE_SIZE ?? 10;
    const maxFiles = currentAgent?.config.VITE_MAX_FILES ?? 5;

    // Avoid passing config if everything is effectively disabled/default
    if (!allowFileUpload && !allowImagePaste && allowedFileTypes.length === 0) {
      return undefined;
    }

    return {
      allowFileUpload,
      allowImagePaste,
      allowedFileTypes,
      maxFileSize,
      maxFiles,
    };
  }, [
    currentAgent?.config.VITE_ALLOW_FILE_UPLOAD,
    currentAgent?.config.VITE_ALLOW_IMAGE_PASTE,
    currentAgent?.config.VITE_ALLOWED_FILE_TYPES,
    currentAgent?.config.VITE_MAX_FILE_SIZE,
    currentAgent?.config.VITE_MAX_FILES,
  ]);

  const recaptchaConfig = useMemo(() => {
    const enabled = currentAgent?.config.VITE_RECAPTCHA_ENABLED ?? false;
    const siteKey = currentAgent?.config.VITE_RECAPTCHA_SITE_KEY?.trim() ?? '';

    if (!enabled || !siteKey) {
      return undefined;
    }

    return {
      enabled,
      siteKey,
      version: currentAgent?.config.VITE_RECAPTCHA_VERSION ?? 'v2',
    } as const;
  }, [
    currentAgent?.config.VITE_RECAPTCHA_ENABLED,
    currentAgent?.config.VITE_RECAPTCHA_SITE_KEY,
    currentAgent?.config.VITE_RECAPTCHA_VERSION,
  ]);

  // Helper function to calculate safe width
  const calculateSafeWidth = useCallback((preferredWidth?: number) => {
    const availableWidth = window.innerWidth - 32; // Account for padding
    const maxWidth = availableWidth * 0.75;
    const minWidth = 600;

    if (preferredWidth) {
      // If we have a preferred width, use it but ensure it's within safe bounds
      return Math.max(minWidth, Math.min(maxWidth, preferredWidth));
    } else {
      // Default to 800px or max available width, whichever is smaller
      return Math.max(minWidth, Math.min(maxWidth, 800));
    }
  }, []);

  // Resize functionality state
  const [chatWidth, setChatWidth] = useState(() => {
    // Try to get saved width from localStorage
    const savedWidth = localStorage.getItem('chat-width');
    const preferredWidth = savedWidth ? parseInt(savedWidth, 10) : undefined;

    return calculateSafeWidth(preferredWidth);
  });
  const [isResizing, setIsResizing] = useState(false);
  const [ghostWidth, setGhostWidth] = useState(0);
  const [isMobile, setIsMobile] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(() => {
    // Initialize fullscreen state from localStorage
    const savedFullscreen = localStorage.getItem('chat-fullscreen');
    return savedFullscreen === 'true';
  });
  const [showEndChatConfirm, setShowEndChatConfirm] = useState(false);
  const chatElementRef = useRef<HTMLDivElement>(null);
  const initialMouseX = useRef(0);

  const dispatch = useAppDispatch();
  const { stopPooling } = usePooling();

  // Notification state and handlers
  const notifications = useNotifications();
  const [dismissNotificationApi, setDismissNotificationApi] = useState<((id: string) => void) | undefined>();
  const [chatContainerWidth, setChatContainerWidth] = useState<number | undefined>(undefined);

  const handleRemoveNotification = useCallback((id: string) => {
    dispatch(removeNotification(id));
  }, [dispatch]);

  const handleMarkAllNotificationsAsRead = useCallback(() => {
    dispatch(markAllNotificationsAsRead());
  }, [dispatch]);

  const handleDismissNotificationApi = useCallback((id: string) => {
    if (dismissNotificationApi) {
      dismissNotificationApi(id);
    }
  }, [dismissNotificationApi]);

  const handleDismissNotificationApiReady = useCallback((apiFunction: (id: string) => void) => {
    setDismissNotificationApi(() => apiFunction);
  }, []);

  // Check if we're on mobile and adjust chat width on window resize
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };

    const adjustChatWidth = () => {
      if (!isMobile && !isFullscreen) {
        // Get saved width from localStorage to respect user preference
        const savedWidth = localStorage.getItem('chat-width');
        const preferredWidth = savedWidth ? parseInt(savedWidth, 10) : chatWidth;

        // Calculate safe width but don't override the stored preference
        const safeWidth = calculateSafeWidth(preferredWidth);
        setChatWidth(safeWidth);
      }
    };

    const handleResize = () => {
      checkMobile();
      adjustChatWidth();
    };

    checkMobile();
    adjustChatWidth();
    window.addEventListener('resize', handleResize);

    return () => window.removeEventListener('resize', handleResize);
  }, [isMobile, chatWidth, calculateSafeWidth, isFullscreen]);

  // Chat hook for clear functionality
  const { clearChat, sessionGuid, isInputDisabledByAgent, isChatEnded } = useChat(undefined, effectiveAgentId, undefined, true, actualLanguage);

  // Track whether the user manually ended the chat in this session
  const userEndedChatRef = useRef(false);

  const handleClearChat = useCallback(() => {
    clearChat();
    dispatch(setChatEndedAction({ agentId: effectiveAgentId, ended: false }));
    dispatch(setLoading({ agentId: effectiveAgentId, loading: false }));
    dispatch(abortRunningMessages(effectiveAgentId));
    stopPooling();
  }, [dispatch, effectiveAgentId, clearChat, stopPooling]);

  // Auto-clear chat when returning to an agent whose chat was previously ended.
  // The end-chat intent replays from IndexedDB (intentProcessed is stripped on save),
  // which re-sets chatEnded=true and creates a deadlock. Detect this and start fresh.
  useEffect(() => {
    if (isChatEnded() && !userEndedChatRef.current) {
      handleClearChat();
    }
  }, [isChatEnded, handleClearChat]);
  // Track chat container width for notification popover
  useEffect(() => {
    const updateWidth = () => {
      if (chatElementRef.current) {
        setChatContainerWidth(chatElementRef.current.offsetWidth);
      }
    };

    updateWidth();

    const resizeObserver = new ResizeObserver(updateWidth);
    if (chatElementRef.current) {
      resizeObserver.observe(chatElementRef.current);
    }

    window.addEventListener('resize', updateWidth);

    return () => {
      resizeObserver.disconnect();
      window.removeEventListener('resize', updateWidth);
    };
  }, []);

  // No selectAgent needed; agent is local

  // Apply agent-specific theme
  useEffect(() => {
    if (!currentAgent) return;

    const root = document.documentElement;
    const config = currentAgent.config;

    // Apply light theme variables
    if (config.VITE_THEME_PRIMARY) {
      root.style.setProperty('--primary', config.VITE_THEME_PRIMARY);
    }
    if (config.VITE_THEME_SECONDARY) {
      root.style.setProperty('--secondary', config.VITE_THEME_SECONDARY);
    }

    // Apply dark theme variables by creating a style element
    let darkThemeStyle = document.getElementById('agent-dark-theme') as HTMLStyleElement;
    if (!darkThemeStyle) {
      darkThemeStyle = document.createElement('style');
      darkThemeStyle.id = 'agent-dark-theme';
      document.head.appendChild(darkThemeStyle);
    }

    // Build dark theme CSS
    let darkThemeCSS = '.dark {';

    // Apply dark primary color
    if (config.VITE_THEME_DARK_PRIMARY && typeof config.VITE_THEME_DARK_PRIMARY === 'string') {
      darkThemeCSS += `--primary: ${config.VITE_THEME_DARK_PRIMARY};`;
    }

    // Apply dark secondary color
    if (config.VITE_THEME_DARK_SECONDARY && typeof config.VITE_THEME_DARK_SECONDARY === 'string') {
      darkThemeCSS += `--secondary: ${config.VITE_THEME_DARK_SECONDARY};`;
    }

    // Apply other dark theme variables
    const darkThemeVars = [
      'VITE_THEME_DARK_BACKGROUND',
      'VITE_THEME_DARK_FOREGROUND',
      'VITE_THEME_DARK_CARD',
      'VITE_THEME_DARK_CARD_FOREGROUND',
      'VITE_THEME_DARK_PRIMARY_FOREGROUND',
      'VITE_THEME_DARK_SECONDARY_FOREGROUND',
      'VITE_THEME_DARK_MUTED',
      'VITE_THEME_DARK_MUTED_FOREGROUND',
      'VITE_THEME_DARK_BORDER',
      'VITE_THEME_DARK_INPUT',
    ];

    darkThemeVars.forEach(varName => {
      const value = config[varName as keyof typeof config];
      if (value && typeof value === 'string') {
        const cssVar = varName.replace('VITE_THEME_DARK_', '--').toLowerCase().replace(/_/g, '-');
        darkThemeCSS += `${cssVar}: ${value};`;
      }
    });

    darkThemeCSS += '}';
    darkThemeStyle.textContent = darkThemeCSS;

    // Apply other light theme variables
    const lightThemeVars = [
      'VITE_THEME_BACKGROUND',
      'VITE_THEME_FOREGROUND',
      'VITE_THEME_CARD',
      'VITE_THEME_CARD_FOREGROUND',
      'VITE_THEME_PRIMARY_FOREGROUND',
      'VITE_THEME_SECONDARY_FOREGROUND',
      'VITE_THEME_MUTED',
      'VITE_THEME_MUTED_FOREGROUND',
      'VITE_THEME_BORDER',
      'VITE_THEME_INPUT',
    ];

    lightThemeVars.forEach(varName => {
      const value = config[varName as keyof typeof config];
      if (value && typeof value === 'string') {
        const cssVar = varName.replace('VITE_THEME_', '--').toLowerCase().replace(/_/g, '-');
        root.style.setProperty(cssVar, value);
      }
    });

    // Set page title - prioritize tenant config over agent config, then translated chat title
    const titleToUse = tenantData?.config?.pageTitle || agentTranslation?.chatTitle || config.VITE_CHAT_TITLE;
    if (titleToUse) {
      document.title = titleToUse;
    }

    // Set favicon if provided
    if (config.VITE_CUSTOM_FAVICON && typeof config.VITE_CUSTOM_FAVICON === 'string') {
      const faviconUrl = config.VITE_CUSTOM_FAVICON as string;
      const existingFavicon = document.querySelector('link[rel="icon"]') as HTMLLinkElement;
      if (existingFavicon) {
        existingFavicon.href = faviconUrl;
      } else {
        const link = document.createElement('link');
        link.rel = 'icon';
        link.type = 'image/png';
        link.href = faviconUrl;
        document.head.appendChild(link);
      }
    }

    // Cleanup function to remove the dynamic style when component unmounts or agent changes
    return () => {
      const darkThemeStyle = document.getElementById('agent-dark-theme');
      if (darkThemeStyle) {
        darkThemeStyle.remove();
      }
    };
  }, [currentAgent, agentTranslation, tenantData]);

  const handleResizeStart = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    initialMouseX.current = e.clientX;
    setGhostWidth(chatWidth);
    setIsResizing(true);
  }, [chatWidth]);

  const toggleFullscreen = useCallback(() => {
    const newFullscreenState = !isFullscreen;
    setIsFullscreen(newFullscreenState);
    // Save fullscreen state to localStorage
    localStorage.setItem('chat-fullscreen', newFullscreenState.toString());

    if (!newFullscreenState) {
      // When exiting fullscreen, restore the saved width
      const savedWidth = localStorage.getItem('chat-width');
      if (savedWidth) {
        const preferredWidth = parseInt(savedWidth, 10);
        const safeWidth = calculateSafeWidth(preferredWidth);
        setChatWidth(safeWidth);
      }
    }
  }, [isFullscreen, calculateSafeWidth]);

  const handleEndChatClick = useCallback(() => {
    setShowEndChatConfirm(true);
  }, []);

  const handleEndChatConfirm = useCallback(async () => {
    if (!currentAgent || isChatEnded()) return;

    try {
      setShowEndChatConfirm(false);
      userEndedChatRef.current = true;

      // 1. Set chat as ended in Redux (blocks UI, persisted via replay)
      dispatch(setChatEndedAction({ agentId: effectiveAgentId, ended: true }));

      // 2. Stop any loading indicators and abort in-flight requests
      dispatch(setLoading({ agentId: effectiveAgentId, loading: false }));
      dispatch(abortRunningMessages(effectiveAgentId));

      // 3. Stop any active pooling
      stopPooling();

      // 4. Add end-chat intent message (persisted to IndexedDB for replay)
      const endChatIntentMessage: Message = {
        id: `end-chat-intent-${Date.now()}`,
        content: '',
        sender: 'agent',
        timestamp: new Date().toISOString(),
        status: 'sent',
        intent: 'end-chat',
        intentProcessed: true, // Mark as already processed to prevent double-processing on replay
        generatedTimestamp: Date.now(),
      };
      dispatch(addMessage({ message: endChatIntentMessage, agentId: effectiveAgentId }));

      // 5. Add system message with "Start new chat" button
      const startNewChatOption: MessageOption = {
        text: 'Start new chat',
        intent: 'custom-intent',
        intentData: JSON.stringify({ action: 'start-new-chat' }),
      };

      const systemMessage: Message = {
        id: `end-chat-system-${Date.now()}`,
        content: 'This chat session has ended. You can no longer send messages.',
        sender: 'system',
        timestamp: new Date().toISOString(),
        status: 'sent',
        generatedTimestamp: Date.now(),
        options: [startNewChatOption],
      };
      dispatch(addMessage({ message: systemMessage, agentId: effectiveAgentId }));

      // 6. Send preemptive end-chat notification to API (fire-and-forget)
      const apiUrl = currentAgent.config.VITE_CHAT_API_URL;
      const agentSid = calculatedAgentSid;

      fetch(apiUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          message: '',
          intent: 'preemptive-end-chat',
          agent_sid: agentSid,
          agent_id: effectiveAgentId,
          session_id: sessionGuid || undefined,
          guid: sessionGuid || undefined,
        }),
      }).catch(err => console.error('Failed to send end-chat notification:', err));

    } catch (error) {
      console.error('Failed to end chat:', error);
    }
  }, [currentAgent, isChatEnded, calculatedAgentSid, effectiveAgentId, sessionGuid, stopPooling, dispatch]);

  const handleEndChatCancel = useCallback(() => {
    setShowEndChatConfirm(false);
  }, []);

  const isEndChatButtonDisabled = isChatEnded() || isInputDisabledByAgent();

  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (!isResizing) return;

      const deltaX = e.clientX - initialMouseX.current;
      let newWidth = chatWidth + deltaX * 2; // Centered resize

      const minWidth = 600;
      const availableWidth = window.innerWidth - 32; // Account for padding
      const maxWidth = availableWidth * 0.75;
      newWidth = Math.max(minWidth, Math.min(maxWidth, newWidth));

      setGhostWidth(newWidth);
    };

    const handleMouseUp = () => {
      if (isResizing) {
        const finalWidth = ghostWidth;
        setChatWidth(finalWidth);
        // Save the user's preferred width to localStorage
        localStorage.setItem('chat-width', finalWidth.toString());
      }
      setIsResizing(false);
      setGhostWidth(0);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };

    if (isResizing) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      document.body.style.cursor = 'ew-resize';
      document.body.style.userSelect = 'none';
    }

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };
  }, [isResizing, chatWidth, ghostWidth]);

  // Show loading spinner while agent is loading or ref intent is processing
  if (agentLoading || refIntentLoading) {
    return (
      <div className="h-full flex items-center justify-center">
        <div className="text-center">
          <Loader2Icon className="w-8 h-8 animate-spin text-primary mx-auto mb-4" />
          <p className="text-muted-foreground">
            {refIntentLoading ? 'Initializing chat with reference data...' : 'Loading agent...'}
          </p>
        </div>
      </div>
    );
  }

  // Show error if agent not found (only after loading is complete)
  if (!currentAgent) {
    return (
      <div className="h-full flex items-center justify-center">
        <div className="text-center max-w-md mx-auto p-6">
          <h2 className="text-xl font-semibold text-foreground mb-2">Agent Not Found</h2>
          <p className="text-muted-foreground mb-6">
            The agent "{agentId}" could not be found.
          </p>
          <button
            onClick={() => navigate('/')}
            className="inline-flex items-center px-4 py-2 bg-primary text-primary-foreground rounded-md hover:bg-primary/90 transition-colors"
          >
            <ArrowLeftIcon className="w-4 h-4 mr-2" />
            Back to Home
          </button>
        </div>
      </div>
    );
  }

  const handleBack = () => {
    navigate('/');
  };

  return (
    <div className="h-full flex flex-col">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-border bg-card">
        <div className="flex items-center space-x-3">
          {!isPublicMode && (
            <button
              onClick={handleBack}
              className="p-2 hover:bg-muted rounded-md transition-colors"
              title="Back to home"
            >
              <ArrowLeftIcon className="w-5 h-5" />
            </button>
          )}
          <div className="flex flex-col space-y-2">
            <div className="flex items-center space-x-2">
              <div>
                <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-purple-600 rounded-full flex items-center justify-center">
                  <MessageCircleIcon className="w-5 h-5 text-primary-foreground" />
                </div>
                <p className="text-xs text-muted-foreground">Online</p>
              </div>
              <div>
                <h1 className="text-xl font-semibold text-foreground pl-4">
                  {agentTranslation?.chatTitle || currentAgent.config.VITE_CHAT_TITLE || agentTranslation?.name}
                </h1>
                {agentTranslation?.description && (
                  <p className="text-sm text-muted-foreground pl-4">{agentTranslation.description}</p>
                )}
              </div>
            </div>
          </div>
        </div>
        {/* Buttons on desktop */}
        {!isMobile && (
          <div className="flex items-center space-x-2">
            {currentAgent?.config.VITE_ENABLE_PREEMPTIVE_END_CHAT_BUTTON && (
              <button
                onClick={handleEndChatClick}
                disabled={isEndChatButtonDisabled}
                className="p-2 rounded-md hover:bg-destructive/10 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                title="End chat"
              >
                <PhoneOffIcon className="w-4 h-4 text-destructive" />
              </button>
            )}
            {currentAgent.config.VITE_ALLOW_NOTIFICATIONS && <NotificationBadge
              notifications={notifications}
              onDismiss={handleRemoveNotification}
              onDismissNotification={handleDismissNotificationApi}
              onMarkAllAsRead={handleMarkAllNotificationsAsRead}
              popoverWidth={chatContainerWidth}
            />
            }
            {currentAgent?.config.VITE_ENABLE_CLEAR_CHAT_BUTTON && (
              <button
                onClick={handleClearChat}
                className="p-2 rounded-md hover:bg-muted transition-colors"
                title="Clear chat"
              >
                <RefreshCwIcon className="w-4 h-4 text-muted-foreground" />
              </button>
            )}
            <button
              onClick={toggleFullscreen}
              className="p-2 rounded-md hover:bg-muted transition-colors"
              title={isFullscreen ? "Exit fullscreen" : "Enter fullscreen"}
            >
              {isFullscreen ? (
                <Minimize2Icon className="w-4 h-4 text-muted-foreground" />
              ) : (
                <Maximize2Icon className="w-4 h-4 text-muted-foreground" />
              )}
            </button>
            {isPublicMode && (
              <button
                onClick={toggleTheme}
                className="p-2 rounded-md hover:bg-muted transition-colors"
                title={theme === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'}
              >
                {theme === 'dark' ? (
                  <SunIcon className="w-4 h-4 text-muted-foreground" />
                ) : (
                  <MoonIcon className="w-4 h-4 text-muted-foreground" />
                )}
              </button>
            )}
          </div>
        )}
      </div>

      {/* Chat Content */}
      <div className="flex-1 overflow-hidden">
        <div className="w-full h-full flex flex-col justify-center items-center">
          <div
            ref={chatElementRef}
            className="relative h-full flex flex-col"
            style={{
              width: isMobile || isFullscreen ? '100%' : `${chatWidth}px`,
              minWidth: isMobile || isFullscreen ? 'auto' : '600px',
              maxWidth: isMobile || isFullscreen ? '100%' : 'none'
            }}
            data-width={chatWidth} // Debug attribute
          >
            <div className="w-full h-full">
              {!currentAgent.config.VITE_CHAT_API_URL ? (
                <div className="text-muted-foreground">No chat API URL configured</div>
              ) : (
                <ChatInterface
                  chatTitle={chatTitle}
                  apiUrl={currentAgent.config.VITE_CHAT_API_URL}
                  enableDarkMode={theme === 'dark'}
                  showDarkModeToggle={false}
                  agentSid={calculatedAgentSid}
                  customFavicon={currentAgent.config.VITE_CUSTOM_FAVICON}
                  feedbackEnabled={currentAgent.config.VITE_FEEDBACK_ENABLED || false}
                  thumbsUpUrl={currentAgent.config.VITE_FEEDBACK_THUMBS_UP_URL}
                  thumbsDownUrl={currentAgent.config.VITE_FEEDBACK_THUMBS_DOWN_URL}
                  feedbackMinChars={currentAgent.config.VITE_FEEDBACK_MIN_CHARS}
                  footnoteEnabled={currentAgent.config.VITE_FOOTNOTE_ENABLED || false}
                  agentMessageFootnote={currentAgent.config.VITE_AGENT_MESSAGE_FOOTNOTE}
                  footnoteTitle={currentAgent.config.VITE_FOOTNOTE_TITLE}
                  footnoteCollapsible={currentAgent.config.VITE_FOOTNOTE_COLLAPSIBLE}
                  footnoteShowTiming={currentAgent.config.VITE_FOOTNOTE_SHOW_TIMING}
                  agentId={effectiveAgentId}
                  streamingConfig={streamingConfig}
                  preferredLanguage={actualLanguage}
                  welcomeMessage={welcomeMessage}
                  hideHeader={true}
                  enablePersistence={currentAgent.config.VITE_ENABLE_PERSISTENCE ?? false}
                  showRouteStack={currentAgent.config.VITE_SHOW_ROUTE_STACK ?? false}
                  fetchForWelcomeMessage={currentAgent.config.VITE_FETCH_FOR_WELCOME_MESSAGE ?? false}
                  allowNotifications={currentAgent.config.VITE_ALLOW_NOTIFICATIONS ?? false}
                  fetchForNotification={currentAgent.config.VITE_FETCH_FOR_NOTIFICATION ?? false}
                  notificationPoolingFrequency={currentAgent.config.VITE_NOTIFICATION_POOLING_FREQUENCY ?? 15}
                  onDismissNotificationApiReady={handleDismissNotificationApiReady}
                  fileUploadConfig={fileUploadConfig}
                  recaptchaConfig={recaptchaConfig}
                />
              )}
            </div>

            {/* Resize Handle - only show on desktop and when not in fullscreen */}
            {!isMobile && !isFullscreen && (
              <div
                className="absolute top-1/2 -right-5 -translate-y-1/2 w-4 h-8 cursor-ew-resize bg-transparent hover:bg-primary/10 transition-colors group z-20 flex items-center justify-center rounded"
                onMouseDown={handleResizeStart}
                title="Drag to resize"
              >
                <div className="transition-opacity">
                  <svg width="32" height="32" viewBox="0 0 15 15" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-3 h-3 text-foreground">
                    <path d="M5.5 2C5.22386 2 5 2.22386 5 2.5C5 2.77614 5.22386 3 5.5 3H6.5C6.77614 3 7 2.77614 7 2.5C7 2.22386 6.77614 2 6.5 2H5.5ZM5.5 5C5.22386 5 5 5.22386 5 5.5C5 5.77614 5.22386 6 5.5 6H6.5C6.77614 6 7 5.77614 7 5.5C7 5.22386 6.77614 5 6.5 5H5.5ZM5 9.5C5 9.22386 5.22386 9 5.5 9H6.5C6.77614 9 7 9.22386 7 9.5C7 9.77614 6.77614 10 6.5 10H5.5C5.22386 10 5 9.77614 5 9.5ZM5.5 12C5.22386 12 5 12.2239 5 12.5C5 12.7761 5.22386 13 5.5 13H6.5C6.77614 13 7 12.7761 7 12.5C7 12.2239 6.77614 12 6.5 12H5.5ZM8.5 2C8.22386 2 8 2.22386 8 2.5C8 2.77614 8.22386 3 8.5 3H9.5C9.77614 3 10 2.77614 10 2.5C10 2.22386 9.77614 2 9.5 2H8.5ZM8.5 5C8.22386 5 8 5.22386 8 5.5C8 5.77614 8.22386 6 8.5 6H9.5C9.77614 6 10 5.77614 10 5.5C10 5.22386 9.77614 5 9.5 5H8.5ZM8 9.5C8 9.22386 8.22386 9 8.5 9H9.5C9.77614 9 10 9.22386 10 9.5C10 9.77614 9.77614 10 9.5 10H8.5C8.22386 10 8 9.77614 8 9.5ZM8.5 12C8.22386 12 8 12.2239 8 12.5C8 12.7761 8.22386 13 8.5 13H9.5C9.77614 13 10 12.7761 10 12.5C10 12.2239 9.77614 12 9.5 12H8.5Z" fill="currentColor" fillRule="evenodd" clipRule="evenodd"></path>
                  </svg>
                </div>
              </div>
            )}

            {/* Ghost resize preview - only show on desktop and when not in fullscreen */}
            {isResizing && !isMobile && !isFullscreen && (
              <div
                className="absolute inset-0 bg-primary/5 border-2 border-primary border-dashed rounded-lg pointer-events-none"
                style={{
                  width: `${ghostWidth}px`,
                  left: '50%',
                  transform: 'translateX(-50%)',
                  zIndex: 1001,
                }}
              />
            )}
          </div>
        </div>
      </div>

      {/* End Chat Confirmation Modal */}
      {showEndChatConfirm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
          <div className="bg-card border border-border rounded-lg shadow-lg max-w-md w-full">
            <div className="p-6">
              <h3 className="text-lg font-semibold text-card-foreground mb-4">End Chat?</h3>
              <p className="text-muted-foreground mb-6">
                Are you sure you want to end this chat? This action will clear the conversation and notify the system.
              </p>
              <div className="flex justify-end space-x-3">
                <button
                  onClick={handleEndChatCancel}
                  className="px-4 py-2 border border-border rounded-md hover:bg-muted transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleEndChatConfirm}
                  className="px-4 py-2 bg-destructive text-destructive-foreground rounded-md hover:bg-destructive/90 transition-colors"
                >
                  End Chat
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
